import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static readonly targets = ['toggle', 'menuItem', 'subMenu'];

  declare toggleTarget: HTMLElement;

  declare menuItemTargets: HTMLElement[];

  declare subMenuTargets: HTMLElement[];

  private isMenuOpen = false;

  private currentMenuItem: HTMLElement | undefined;

  private currentSubMenu: HTMLElement | undefined;

  private keepSubMenuOpen = false;

  initialize() {
    this.currentMenuItem = this.menuItemTargets[0];
    this.currentSubMenu = this.subMenuTargets[0];

    this.element.addEventListener('focusout', (event) => {
      const focusEvent: FocusEvent = event as FocusEvent;
      if (!this.element.contains(focusEvent.relatedTarget as Node)) {
        this.currentMenuItem?.setAttribute('tabindex', '0');
        this.keepSubMenuOpen = false;
        this.toggleSubMenu(false);
      }
    });

    this.handleMenuKeypress();
    this.handleSubMenuKeypress();
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    this.toggleTarget.setAttribute('aria-expanded', this.isMenuOpen.toString());
  }

  private handleMenuKeypress() {
    for (const [index, menuItem] of this.menuItemTargets.entries()) {
      menuItem.addEventListener('keydown', (event) => {
        switch (event.key) {
          case 'ArrowRight':
          case 'Right': {
            this.moveToRightMenuItem(index);
            this.focusNextMenuItem(event.currentTarget as HTMLElement);
            event.preventDefault();
            break;
          }
          case 'ArrowLeft':
          case 'Left': {
            this.moveToLeftMenuItem(index);
            this.focusNextMenuItem(event.currentTarget as HTMLElement);
            event.preventDefault();
            break;
          }
          case 'ArrowUp':
          case 'Up':
          case 'End':
          case 'PageDown': {
            this.keepSubMenuOpen = true;
            this.currentSubMenu = this.subMenuTargets[index];
            this.showSubMenu(true);
            event.preventDefault();
            break;
          }
          case 'ArrowDown':
          case 'Down':
          case 'Home':
          case 'PageUp':
          case ' ':
          case 'Enter': {
            this.keepSubMenuOpen = true;
            this.currentSubMenu = this.subMenuTargets[index];
            this.showSubMenu();
            event.preventDefault();
            break;
          }
          default: {
            break;
          }
        }
      });
    }
  }

  private handleSubMenuKeypress() {
    for (const [subMenuIndex, subMenu] of this.subMenuTargets.entries()) {
      const subMenuItems = this.getSubMenuItems(subMenu);
      for (const [subMenuItemIndex, subMenuItem] of subMenuItems.entries()) {
        subMenuItem.addEventListener('keydown', (event) => {
          switch (event.key) {
            case 'ArrowDown':
            case 'Down': {
              const nextSubMenuItem =
                subMenuItemIndex === subMenuItems.length - 1
                  ? subMenuItems[0]
                  : subMenuItems[subMenuItemIndex + 1];
              nextSubMenuItem.focus();
              event.preventDefault();
              break;
            }
            case 'ArrowUp':
            case 'Up': {
              const nextSubMenuItem =
                subMenuItemIndex === 0
                  ? subMenuItems.at(-1)
                  : subMenuItems[subMenuItemIndex - 1];
              nextSubMenuItem?.focus();
              event.preventDefault();
              break;
            }
            case 'ArrowRight':
            case 'Right': {
              const previousMenuItem = this.currentMenuItem;
              this.toggleSubMenu(false);
              this.moveToRightMenuItem(subMenuIndex);
              if (previousMenuItem) {
                this.focusNextMenuItem(previousMenuItem);
              }
              this.currentSubMenu =
                subMenuIndex === this.subMenuTargets.length - 1
                  ? this.subMenuTargets[0]
                  : this.subMenuTargets[subMenuIndex + 1];
              this.showSubMenu();
              event.preventDefault();
              break;
            }
            case 'ArrowLeft':
            case 'Left': {
              const previousMenuItem = this.currentMenuItem;
              this.toggleSubMenu(false);
              this.moveToLeftMenuItem(subMenuIndex);
              if (previousMenuItem) {
                this.focusNextMenuItem(previousMenuItem);
              }
              this.currentSubMenu =
                subMenuIndex === 0
                  ? (this.subMenuTargets.at(-1) as HTMLElement)
                  : this.subMenuTargets[subMenuIndex - 1];
              this.showSubMenu();
              event.preventDefault();
              break;
            }
            case 'Escape':
            case 'Esc': {
              this.keepSubMenuOpen = false;
              this.toggleSubMenu(false);
              this.currentMenuItem?.focus();
              event.preventDefault();
              break;
            }
            default: {
              break;
            }
          }
        });
      }
    }
  }

  private moveToRightMenuItem(menuItemIndex: number) {
    this.currentMenuItem =
      menuItemIndex === this.menuItemTargets.length - 1
        ? this.menuItemTargets[0]
        : this.menuItemTargets[menuItemIndex + 1];
  }

  private moveToLeftMenuItem(menuItemIndex: number) {
    this.currentMenuItem =
      menuItemIndex === 0
        ? (this.menuItemTargets.at(-1) as HTMLElement)
        : this.menuItemTargets[menuItemIndex - 1];
  }

  private focusNextMenuItem(previousMenuItem: HTMLElement) {
    previousMenuItem.setAttribute('tabindex', '-1');
    this.currentMenuItem?.focus();
  }

  private getSubMenuItems(subMenu: HTMLElement): HTMLElement[] {
    return [...subMenu.querySelectorAll('a[role="menuitem"]')] as HTMLElement[];
  }

  private showSubMenu(showLastItem = false) {
    if (this.keepSubMenuOpen && this.currentSubMenu) {
      this.toggleSubMenu(true);
      this.currentMenuItem?.setAttribute('tabindex', '-1');
      const subMenuItems = this.getSubMenuItems(this.currentSubMenu);
      if (showLastItem) {
        subMenuItems?.at(-1)?.focus();
      } else {
        subMenuItems[0].focus();
      }
    }
  }

  private toggleSubMenu(isExpanded: boolean) {
    this.currentMenuItem?.setAttribute('aria-expanded', isExpanded.toString());
  }
}
